import React, { useState } from "react";
import CommonButton from "../../../components/CommonButton";
import SelectPetsSection from "../../../components/customer/SelectPetsSection";
import Carousel from "../../../components/customer/Carousel";
import {
  catbreedOptionsMap,
  dogbreedOptionsMap,
  petOptions,
  taxiImage,
  userPets,
} from "../../../utils/Customer/CustomerData";
import { useNavigate } from "react-router-dom";
import { ChevronDown, MapPin } from "lucide-react";
import Dropdown, { Option } from "../../../components/customer/Dropdown";
import TextInputComp from "../../../components/customer/TextInputComp";
import Autocomplete from "react-google-autocomplete";
import { MAPS_KEY } from "../../../config/apiUrlsCustomer";

const MobileGroomingScreen: React.FC = () => {
  const [selectedPetType, setSelectedPetType] = useState<any>(null);
  const [selectedSavedPet, setSelectedSavedPet] = useState<any>(null);
  const [selectedBreed, setSelectedBreed] = useState<Option | null>(null);
  const [breedInput, setBreedInput] = useState("");
  const [weight, setWeight] = useState("");
  const [latitude, setLatitude] = useState(0);
  const [longitude, setLongitude] = useState(0);
  const [city, setCity] = useState("");
  const [stateName, setStateName] = useState("");
  const [country, setCountry] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [address, setAddress] = useState("");
  const [dropAddress, dropSetAddress] = useState("");
  const [droplatitude, dropSetLatitude] = useState(0);
  const [dropLongitude, dropSetLongitude] = useState(0);
  const [dropCity, dropSetCity] = useState("");
  const [dropStateName, dropSetStateName] = useState("");
  const [dropCountry, dropSetCountry] = useState("");
  const [dropPostalCode, dropSetPostalCode] = useState("");

  const navigate = useNavigate();

  const handlePetTypeSelect = (pet: any) => {
    setSelectedPetType(pet);
    setSelectedBreed(null);
    setBreedInput("");
  };

  const handleSavedPetSelect = (pet: any) => {
    setSelectedSavedPet(pet);
  };

  const getBreedOptions = () => {
    switch (selectedPetType?.value?.toLowerCase()) {
      case "dog":
        return dogbreedOptionsMap;

      case "cat":
        return catbreedOptionsMap;

      default:
        return [];
    }
  };

  const handleBooking = () => {
    navigate("/productpage/customergroomingbookingdatescreen", {
      state: {
        serviceType: "mobilegrooming",
        bookingData: {
          petType: selectedPetType,
          pet: selectedSavedPet,
          breed: selectedBreed,
          weight,
          pickup: {
            address,
            city,
            state: stateName,
            country,
            postalCode,
            latitude,
            longitude,
          },

          drop: {
            address: dropAddress,
            city: dropCity,
            state: dropStateName,
            country: dropCountry,
            postalCode: dropPostalCode,
            latitude: droplatitude,
            longitude: dropLongitude,
          },
        },
      },
    });
  };

  return (
    <div className="bg-white min-h-screen pb-20">
      {/* HERO CAROUSEL BANNER */}
      <div className="relative h-[320px] overflow-hidden">
        <Carousel items={taxiImage} />
        <div className="absolute inset-0 bg-black/50 z-10" />
        <div className="absolute inset-0 z-20 flex items-center justify-center px-6">
          <div className="text-center">
            <h1 className="text-white text-5xl md:text-6xl font-light tracking-tight">
              Book a Taxi
            </h1>
            <p className="text-white/80 mt-3 text-lg">
              Safe, comfortable, and reliable rides for your furry companion
            </p>
          </div>
        </div>
      </div>

      {/* CORE FLOW CONTAINER */}
      <div className="max-w-7xl mx-auto px-4 pt-10 space-y-8">
        <div className="bg-white rounded-[2.5rem] p-8 md:p-10 shadow-xl border border-[#ECE7DF] space-y-10">
          <div className="flex flex-col lg:flex-row lg:items-stretch w-full gap-5 lg:gap-12">
            <SelectPetsSection
              title="Select Pet Type"
              subtitle="Choose your matching standard pet category"
              data={petOptions.slice(0, 2)}
              selectedId={selectedPetType?.id}
              onSelect={handlePetTypeSelect}
              imageSize="w-24 h-24"
            />

            <div className="border-l-2 border-dashed border-gray-300 self-stretch" />

            <SelectPetsSection
              title="Who's getting pampered?"
              subtitle="Select a profile or add a new furry friend to your family."
              data={userPets}
              selectedId={selectedSavedPet?.id}
              onSelect={handleSavedPetSelect}
              imageSize="w-28 h-28"
              showAddButton
              onAddClick={() => navigate("/add-new-pet")}
            />
          </div>
          <div className="flex items-center justify-between gap-4">
            {/* Breed Dropdown */}
            <div className="flex flex-1">
              <Dropdown
                showLabel
                label="Select Breed"
                options={getBreedOptions()}
                value={selectedBreed}
                onChange={(option) => {
                  setSelectedBreed(option);
                  setBreedInput(option.label);
                }}
                dropdownIcon={<ChevronDown size={16} />}
                innerClassName="border-[#A8D97B] rounded-xl shadow-sm"
                className="w-full"
              />
            </div>

            <div className="flex flex-col flex-1">
              <TextInputComp
                value={weight}
                onChangeText={setWeight}
                showLabel={true}
                label="Weight (KG)"
                type="number"
                placeholder="Enter Weight (Ex:2.6)"
              />
            </div>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-iconColor mb-5">
              Address Information (Pickup Location)
            </h3>

            <div className="mb-3">
              <label className="text-md text-mainBrandColor mb-2 block">
                Address
              </label>

              <div className="relative">
                <MapPin
                  size={18}
                  className="absolute left-4 top-4 text-[#6DBE45] z-10"
                />
                <Autocomplete
                  apiKey={MAPS_KEY}
                  className="w-full border rounded-xl py-3 pl-12 pr-4"
                  placeholder="Search Address..."
                  defaultValue={address}
                  options={{
                    componentRestrictions: { country: "ae" },
                    types: ["geocode"],
                  }}
                  onPlaceSelected={(place: any) => {
                    const components = place.address_components || [];

                    const getComponent = (type: string) =>
                      components.find((c: any) => c.types.includes(type))
                        ?.long_name || "";

                    setAddress(place.formatted_address || "");
                    setCity(getComponent("locality"));
                    setStateName(getComponent("administrative_area_level_1"));
                    setCountry(getComponent("country"));
                    setPostalCode(getComponent("postal_code"));

                    if (place.geometry?.location) {
                      setLatitude(place.geometry.location.lat());
                      setLongitude(place.geometry.location.lng());
                    }
                  }}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-x-5">
              <TextInputComp
                value={city}
                onChangeText={setCity}
                placeholder="City"
                label="City"
                showLabel
              />

              <TextInputComp
                value={stateName}
                onChangeText={setStateName}
                placeholder="State"
                label="State"
                showLabel
              />

              <TextInputComp
                value={country}
                onChangeText={setCountry}
                placeholder="Country"
                label="Country"
                showLabel
              />

              <TextInputComp
                value={postalCode}
                onChangeText={setPostalCode}
                placeholder="Postal Code"
                label="Postal Code"
                showLabel
              />
            </div>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-iconColor mb-5">
              Address Information (Drop Location)
            </h3>

            <div className="mb-3">
              <label className="text-md text-mainBrandColor mb-2 block">
                Address
              </label>

              <div className="relative">
                <MapPin
                  size={18}
                  className="absolute left-4 top-4 text-[#6DBE45] z-10"
                />

                <Autocomplete
                  apiKey={MAPS_KEY}
                  className="w-full border rounded-xl py-3 pl-12 pr-4"
                  placeholder="Search Address..."
                  defaultValue={dropAddress}
                  options={{
                    componentRestrictions: { country: "ae" },
                    types: ["geocode"],
                  }}
                  onPlaceSelected={(place: any) => {
                    const components = place.address_components || [];

                    const getComponent = (type: string) =>
                      components.find((c: any) => c.types.includes(type))
                        ?.long_name || "";

                    dropSetAddress(place.formatted_address || "");
                    dropSetCity(getComponent("locality"));
                    dropSetStateName(
                      getComponent("administrative_area_level_1")
                    );
                    dropSetCountry(getComponent("country"));
                    dropSetPostalCode(getComponent("postal_code"));

                    if (place.geometry?.location) {
                      dropSetLatitude(place.geometry.location.lat());
                      dropSetLongitude(place.geometry.location.lng());
                    }
                  }}
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-x-5">
              <TextInputComp
                value={dropCity}
                onChangeText={dropSetCity}
                placeholder="City"
                label="City"
                showLabel
              />

              <TextInputComp
                value={dropStateName}
                onChangeText={dropSetStateName}
                placeholder="State"
                label="State"
                showLabel
              />

              <TextInputComp
                value={dropCountry}
                onChangeText={dropSetCountry}
                placeholder="Country"
                label="Country"
                showLabel
              />

              <TextInputComp
                value={dropPostalCode}
                onChangeText={dropSetPostalCode}
                placeholder="Postal Code"
                label="Postal Code"
                showLabel
              />
            </div>
          </div>

          {/* Continue Button */}
          <div className="flex justify-center md:justify-end pt-4 border-t border-gray-100">
            <CommonButton
              onClick={handleBooking}
              text="Continue"
              variant="filled"
              className="w-full md:w-auto px-10 py-2 rounded-full transition-all duration-300"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileGroomingScreen;
